import { useState, useRef } from 'react';
import { ArrowLeft, ArrowRight, Upload, Trash2 } from 'lucide-react';
import { createBranding } from '../../services/brandingService';
import './styles/PlatformConfiguration.css';

interface Props {
  nextStep: () => void;
  previousStep: () => void;
  organizationId: string;
}

const Branding = ({ nextStep, previousStep, organizationId }: Props) => {
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('/logo192.png');
  const [primaryColor, setPrimaryColor] = useState('#5bc48c');
  const [secondaryColor, setSecondaryColor] = useState('#5bc48c');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleLogoUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleLogoDelete = () => {
    setLogoFile(null);
    setLogoPreview('/logo192.png');
  };

  const handleNext = async () => {
    setError(null);

    if (!logoFile) {
      nextStep();
      return;
    }

    try {
      setIsSaving(true);
      await createBranding({
        organizationId,
        logo: logoFile,
        primaryColor,
        secondaryColor,
      });
      nextStep();
    } catch (err) {
      console.error('Failed to save branding:', err);
      setError('Failed to save branding. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="organization-wrapper">
      {/* Header */}
      <div className="branding-header">
        <h2 className="branding-title">Branding</h2>
        <p className="branding-subtitle">Logo and brand colors</p>
      </div>

      {/* Logo */}
      <div className="branding-section">
        <label className="branding-label">ORGANIZATION LOGO</label>

        <div className="branding-logo-row">
          <div className="branding-logo-preview">
            <img src={logoPreview} alt="" />
          </div>

          <div className="branding-upload">
            <div className="branding-upload-top">
              <input ref={fileInputRef} type="file" accept=".png,.jpg,.jpeg,.svg" onChange={handleLogoChange} className="hidden" />

              <button className="branding-upload-btn" onClick={handleLogoUploadClick}>
                <Upload size={14} />
                <span>Upload Logo</span>
              </button>

              <button className="branding-delete-btn" onClick={handleLogoDelete}>
                <Trash2 size={11} />
              </button>
            </div>

            <p className="branding-logo-help">
              PNG, SVG or JPG recommended.
              <br />
              Shown in sidebar.
            </p>
          </div>
        </div>
      </div>

      {/* Brand Color */}
      <div className="branding-section">
        <label className="branding-label">PRIMARY BRAND COLOR</label>

        <div className="branding-color-row">
          <input type="text" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} className="branding-color-input" />

          <div className="branding-color-circle" style={{ background: primaryColor }} />
        </div>

        <p className="branding-color-help">Applied to sidebar, primary buttons, and tabs.</p>
      </div>

      {/* Preview */}
      <div className="branding-section">
        <label className="branding-label">PREVIEW</label>

        <div className="branding-preview">
          <button className="branding-sidebar-btn">Active menu item</button>
          <button className="branding-primary-btn">Primary Button</button>
        </div>
      </div>

      {error && <p className="text-red-500 text-sm mt-2">{error}</p>}

      {/* Footer */}
      <div className="organization-footer">
        <button onClick={previousStep} className="organization-back-btn">
          <ArrowLeft size={18} />
          Back
        </button>

        <div className="organization-footer-right">
          <button className="organization-save-btn" disabled={isSaving}>
            Save & Edit
          </button>

          <button onClick={handleNext} className="organization-next-btn" disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Next'}
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Branding;
